import { useState, useCallback } from 'react';
import { apiPut, ApiResponse, ApiError } from '../../services/api.service';

interface UseLeaveGroupReturn {
  leaveGroup: () => Promise<boolean>;
  loading: boolean;
  error: string | null;
  success: boolean;
}

/**
 * Custom hook to leave the current group
 * @returns Function to leave group, loading state, error state, and success state
 */
export function useLeaveGroup(): UseLeaveGroupReturn {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);

  const leaveGroup = useCallback(async (): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const response: ApiResponse<{ message: string }> = await apiPut<{ message: string }>(
        '/groups/leave',
        {},
        {
          notification: {
            enabled: true,
            successMessage: 'Successfully left the group',
            errorMessage: 'Failed to leave group. Please try again.',
          },
        }
      );

      if (response.success) {
        setSuccess(true);
        return true;
      } else {
        setError(response.error || 'Failed to leave group');
        return false;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    leaveGroup,
    loading,
    error,
    success,
  };
}